// Initiative type configurations

export type InitiativeType =
  | 'quiz'
  | 'campaign'
  | 'competition'
  | 'giveaway'
  | 'promotion'
  | 'event'
  | 'course'
  | 'activation'
  | 'workshop'
  | 'collaboration'
  | 'role'
  | 'webinar'
  | 'officehours'
  | 'program';

export interface InitiativeTypeConfig {
  label: string;
  color: string;
  description?: string;
  mainPageUrl?: (id: number) => string;
}

export const INITIATIVE_TYPE_CONFIG: Record<InitiativeType, InitiativeTypeConfig> = {
  quiz: {
    label: 'Quiz',
    color: 'bg-purple-100 text-purple-800',
    description: 'Create an interactive quiz with custom questions and settings',
    mainPageUrl: (id: number) => `/quizzes/${id}`,
  },
  officehours: {
    label: 'Office Hours',
    color: 'bg-gray-100 text-gray-800',
    description: 'Office Hours',
  },
  program: {
    label: 'Program',
    color: 'bg-indigo-100 text-indigo-800',
    description: 'Plan + initiatives to achieve some outcome over time',
  },
  workshop: {
    label: 'Workshop',
    color: 'bg-amber-100 text-amber-800',
    description: 'Workshop - in person or online',
  },
  campaign: {
    label: 'Campaign',
    color: 'bg-blue-100 text-blue-800',
    description: 'Launch a marketing campaign for your products',
  },
  collaboration: {
    label: 'Collaboration',
    color: 'bg-cyan-100 text-cyan-800',
    description: 'Collaborate with other brands and businesses',
  },
  giveaway: {
    label: 'Giveaway',
    color: 'bg-pink-100 text-pink-800',
    description: 'A giveaway usually requiring entry',
  },
  competition: {
    label: 'Competition',
    color: 'bg-orange-100 text-orange-800',
    description: 'Run a competition to engage your audience',
  },
  promotion: {
    label: 'Promotion',
    color: 'bg-green-100 text-green-800',
    description: 'Offer special discounts and promotions',
  },
  event: {
    label: 'Event',
    color: 'bg-indigo-100 text-indigo-800',
    description: 'Organize an event for your brand or products',
  },
  course: {
    label: 'Course',
    color: 'bg-emerald-100 text-emerald-800',
    description: 'Course - online or in-person',
  },
  activation: {
    label: 'Activation',
    color: 'bg-yellow-100 text-yellow-800',
    description: 'Create a brand activation experience',
  },
  role: {
    label: 'Role',
    color: 'bg-teal-100 text-teal-800',
    description: 'Post a job hire or role opportunity',
  },
  webinar: {
    label: 'Webinar',
    color: 'bg-red-100 text-red-800',
    description: 'Host an online webinar or live session',
  },
} as const;

// Helper function to get initiative type config
export function getInitiativeTypeConfig(type: string): InitiativeTypeConfig {
  return (
    INITIATIVE_TYPE_CONFIG[type as InitiativeType] || {
      label: type || 'Initiative',
      color: 'bg-gray-100 text-gray-800',
    }
  );
}

// Helper function to get initiative URL
export function getInitiativeUrl(type: string, id: number): string {
  const config = getInitiativeTypeConfig(type);
  return config.mainPageUrl ? config.mainPageUrl(id) : `/initiatives/${id}`;
}

// Initiative categories
export const INITIATIVE_CATEGORY_OPTIONS = [
  { id: 'trade', label: 'Trade' },
  { id: 'consumer', label: 'Consumer' },
  { id: 'workplace', label: 'Workplace' },
] as const;

// Initiative activities
export const INITIATIVE_TAG_OPTIONS: { id: string; label: string; scopes?: string[] }[] = [
  { id: 'product_sampling', label: 'Product Sampling', scopes: ['activity'] },
  { id: 'product_demo', label: 'Product Demonstration', scopes: ['activity'] },
  { id: 'pop_up', label: 'Pop-up Stall / Booth' },
  { id: 'promotional_giveaway', label: 'Promotional Giveaway' },
  { id: 'workshop', label: 'Workshop', scopes: ['activity'] },
  { id: 'event_sponsorship', label: 'Event Sponsorship' },
  { id: 'brand_display', label: 'Brand Display / Installation' },
  { id: 'cooking_demo', label: 'Cooking Demo', scopes: ['activity'] },
  { id: 'product_launch', label: 'Product Launch' },
] as const;

// Activation role options
export const ACTIVATION_ROLE_OPTIONS = [
  { id: 'host', label: 'Host Activations' },
  { id: 'run', label: 'Run Activations' },
  { id: 'sponsor', label: 'Sponsor Activations' },
  { id: 'collaborate', label: 'Co-create Activations' },
  { id: 'supply_products', label: 'Provide Products' },
  { id: 'support_services', label: 'Support Services / Logistics' },
] as const;

// Activation environment options
export const ACTIVATION_ENVIRONMENT_OPTIONS = [
  { id: 'retail_store', label: 'Retail Store' },
  { id: 'workplace', label: 'Workplace / Office' },
  { id: 'public_venue', label: 'Public Venue' },
  { id: 'event_festival', label: 'Event / Festival' },
  { id: 'hospitality_venue', label: 'Hospitality Venue' },
  { id: 'tourism_destination', label: 'Tourism Destination' },
  { id: 'university_campus', label: 'University / Campus' },
  { id: 'gym_sports', label: 'Gym / Sports Venue' },
  { id: 'transport_hub', label: 'Transport Hub' },
] as const;

// Activation audience profile options
export const ACTIVATION_AUDIENCE_OPTIONS = [
  { id: 'staff', label: 'Staff / Employees' },
  { id: 'students', label: 'Students' },
  { id: 'families', label: 'Families' },
  { id: 'tourists', label: 'Tourists' },
  { id: 'office_professionals', label: 'Office Professionals' },
  { id: 'local_residents', label: 'Local Residents' },
  { id: 'event_attendees', label: 'Event Attendees' },
  { id: 'members_only', label: 'Members Only' },
] as const;

// Activation capabilities (facilities/equipment)
export const ACTIVATION_CAPABILITY_OPTIONS = [
  { id: 'indoor_space', label: 'Indoor Space' },
  { id: 'outdoor_space', label: 'Outdoor Space' },
  { id: 'table_space', label: 'Table / Booth Space' },
  { id: 'power', label: 'Power Access' },
  { id: 'water', label: 'Water Access' },
  { id: 'kitchen', label: 'Kitchen Access' },
  { id: 'storage', label: 'Storage Space' },
  { id: 'fridge_freezer', label: 'Fridge / Freezer' },
  { id: 'av_equipment', label: 'AV Equipment' },
  { id: 'wifi', label: 'WiFi' },
] as const;

// Activation terms (commercial model)
export const ACTIVATION_TERMS_OPTIONS = [
  { id: 'free', label: 'Free' },
  { id: 'paid', label: 'Paid' },
  { id: 'case_by_case', label: 'Case by Case' },
] as const;

// Activation requirements
export const ACTIVATION_REQUIREMENT_OPTIONS = [
  { id: 'insurance_required', label: 'Insurance Required' },
  { id: 'food_safety', label: 'Food Safety Compliance' },
  { id: 'pre_approval', label: 'Pre-approval Required' },
  { id: 'must_provide_staff', label: 'Must Provide Staff' },
  { id: 'must_supply_equipment', label: 'Must Supply Equipment' },
  { id: 'follow_venue_guidelines', label: 'Must Follow Venue Guidelines' },
] as const;

// Activation availability
export const ACTIVATION_AVAILABILITY_OPTIONS = [
  { id: 'anytime', label: 'Anytime' },
  { id: 'weekdays', label: 'Weekdays' },
  { id: 'weekends', label: 'Weekends' },
  { id: 'specific_events', label: 'Specific Events' },
  { id: 'seasonal', label: 'Seasonal' },
  { id: 'on_request', label: 'On Request' },
] as const;

// Activation promotion support
export const ACTIVATION_PROMOTION_OPTIONS = [
  { id: 'social_media', label: 'Social Media Promotion' },
  { id: 'email_newsletter', label: 'Email Newsletter' },
  { id: 'in_store_signage', label: 'In-store Signage' },
  { id: 'website_listing', label: 'Website Listing' },
  { id: 'event_promotion', label: 'Event Promotion' },
] as const;

// Brand category preferences for activations
export const PRODUCT_CATEGORY_OPTIONS = [
  { id: 'food', label: 'Food' },
  { id: 'beverage', label: 'Beverage' },
  { id: 'alcohol', label: 'Alcohol' },
  { id: 'health_wellness', label: 'Health & Wellness' },
  { id: 'lifestyle', label: 'Lifestyle' },
  { id: 'beauty', label: 'Beauty' },
  { id: 'pet', label: 'Pet' },
] as const;

// Initiative occasion options
export const INITIATIVE_OCCASION_OPTIONS = [
  { id: 'kids_party', label: 'Kids Party' },
  { id: 'orientation_week', label: 'Orientation Week' },
  { id: 'corporate_event', label: 'Corporate Event' },
  { id: 'team_building', label: 'Team Building' },
  { id: 'product_launch', label: 'Product Launch' },
  { id: 'networking', label: 'Networking' },
  { id: 'conference', label: 'Conference' },
  { id: 'trade_show', label: 'Trade Show' },
  { id: 'festival', label: 'Festival' },
  { id: 'market_day', label: 'Market Day' },
  { id: 'open_day', label: 'Open Day' },
  { id: 'fundraiser', label: 'Fundraiser' },
  { id: 'community_event', label: 'Community Event' },
  { id: 'sports_event', label: 'Sports Event' },
  { id: 'wellness_event', label: 'Wellness Event' },
  { id: 'seasonal_celebration', label: 'Seasonal Celebration' },
  { id: 'pop_up', label: 'Pop-up' },
] as const;

// Demographics age group options
export const DEMOGRAPHICS_AGE_GROUP_OPTIONS = [
  { id: '0-10', label: '0 - 10 years' },
  { id: '11-17', label: '11 - 17 years' },
  { id: '18-24', label: '18 - 25 years' },
  { id: '25-45', label: '25 - 45 years' },
  { id: '45-60', label: '45 - 60 years' },
  { id: '60-80', label: '60 - 80 years' },
  { id: '80+', label: '80+ years' },
] as const;

// Demographics audience type options
export const DEMOGRAPHICS_GENDER_MIX_OPTIONS = [
  { id: '', label: 'Not Specified' },
  { id: 'mostly_female', label: 'Mostly female' },
  { id: 'mostly_male', label: 'Mostly male' },
  { id: 'balanced', label: 'Balanced' },
  { id: 'diverse', label: 'Diverse' },
] as const;

// Initiative statuses that are publicly visible (no auth/permission required)
export const INITIATIVE_PUBLIC_STATUSES = ['active', 'gauging'] as const;

/** Check if an initiative status is publicly visible */
export function isPublicInitiativeStatus(status: string): boolean {
  return (INITIATIVE_PUBLIC_STATUSES as readonly string[]).includes(status);
}
